import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';

import { useCreateOrderOnDemand, useInitializeOrderOnDemand } from '@api';
import { useUserContext } from '@context/UserContext';
import { useForceUpdate } from '@hooks';
import { EOrderState, EOrderAsLastTimeState, IOnSuccessProps } from '@model';
import { useTrackCheckoutCloseRepeatedPurchase, EcommerceSectionNamespace } from '@services/ecommerceService';
import { useRedirect } from '@services/routingService';
import { Loader } from '@sharedComponents/Loader';
import { EPage } from '@types';
import { getAdyenPaymentType } from '@utils/payment';

import { mapOrderToAdyenOrder } from '../../../../adyen';
import { useCartPageContext } from '../../../../context';
import { createEcommerceOaltCheckout } from '../../../../ecommerce';
import { ORDER_AS_LAST_TIME_LOADER_SIZE } from '../../constants';
import { IOaLTProcessProps, TOaLTProcessOutput } from '../../model';
import { generalMessages } from '../generalMessages';

import { adyenMessages } from './adyenMesssages';
import { useHandleOrderCreation } from './useHandleOrderCreation';

const AdyenPaymentComponent = lazy(() => import('@services/paymentService').then((module) => module.AdyenV6));

export const useAdyenProcessForOaLT = ({
  setState,
  lastOrder,
  cartPrice,
  clientKey,
  onClose,
}: IOaLTProcessProps & { clientKey?: string; onClose: () => void }): TOaLTProcessOutput => {
  const {
    state: { cart, customerGuid },
  } = useUserContext();

  const {
    state: { pricesSummary, products, serviceItems },
    settings: { adyen: adyenPaymentSettings },
  } = useCartPageContext();

  const { request: creatOrder } = useCreateOrderOnDemand();
  const { handleOrderCreation } = useHandleOrderCreation();
  const { request: initializeOrder } = useInitializeOrderOnDemand();
  const [orderId, setOrderId] = useState<number>(0);
  const redirect = useRedirect();
  const [key, forceUpdate] = useForceUpdate();
  const trackCloseOalt = useTrackCheckoutCloseRepeatedPurchase();

  const onCloseButton = useCallback(() => {
    if (lastOrder) {
      trackCloseOalt(
        createEcommerceOaltCheckout(lastOrder, products, pricesSummary, serviceItems),
        EOrderAsLastTimeState.ERROR,
      );
    }
    onClose();
  }, [onClose, trackCloseOalt, lastOrder, products, pricesSummary, serviceItems]);

  return useMemo(() => {
    const onSuccess = async (data: IOnSuccessProps) => {
      if (!data?.orderId) {
        return;
      }
      await handleOrderCreation(data.orderId, onClose, data.setComponentState);
    };

    return {
      actionComponent: lastOrder && clientKey && (
        <Suspense fallback={<Loader height={ORDER_AS_LAST_TIME_LOADER_SIZE} />}>
          <AdyenPaymentComponent
            key={key}
            orderInfo={mapOrderToAdyenOrder(lastOrder, orderId, cartPrice, cart, customerGuid)}
            clientKey={clientKey}
            onSuccess={onSuccess}
            paymentType={getAdyenPaymentType(lastOrder.paymentInfo.providerType)}
            paymentId={lastOrder.paymentInfo.paymentType}
            messages={{ ...adyenMessages, ...generalMessages }}
            onRepeatPayment={forceUpdate}
            onReturnToPreviousStep={onCloseButton}
            setPageState={setState}
            adyenPaymentSettings={adyenPaymentSettings}
            provider={EcommerceSectionNamespace.EEcommercePaymentProvider.ADYEN}
            providerType={lastOrder.paymentInfo.providerType}
            userEmail={lastOrder.invoiceAddress.email}
            isRepay={false}
          />
        </Suspense>
      ),
      onContinueToCheckout: async () => {
        if (!lastOrder) {
          return;
        }
        const result = await initializeOrder({
          deliveryInfo: lastOrder.deliveryInfo,
          deliveryAddress: lastOrder.deliveryAddress,
          invoiceAddress: lastOrder.invoiceAddress,
          paymentInfo: {
            paymentType: lastOrder.paymentInfo.paymentType,
            paymentPrice: lastOrder.paymentInfo.paymentPrice,
            paymentName: lastOrder.paymentInfo.paymentName,
            paymentGroups: lastOrder.paymentInfo.paymentGroups,
          },
          sameDeliveryAndInvoice: lastOrder.sameDeliveryAndInvoice,
        });

        if (!result) {
          return;
        }

        if (result.orderState === EOrderState.Complete) {
          onClose();
          redirect(EPage.THANK_YOU);
          return;
        }

        setOrderId(result.orderId);
        setState(EOrderAsLastTimeState.ACTION_PAGE);
      },
    };
  }, [
    lastOrder,
    clientKey,
    key,
    orderId,
    cartPrice,
    cart,
    customerGuid,
    forceUpdate,
    onCloseButton,
    setState,
    adyenPaymentSettings,
    handleOrderCreation,
    onClose,
    creatOrder,
    redirect,
    initializeOrder,
  ]);
};
